import { LegalDocuments } from '../../shared/LegalDocuments.tsx';
import { useEffect, useState, type FormEvent } from 'react';
import { useServices } from '../../core/services.tsx';
import { Button, Notice, TextInput, Checkbox } from '../../ui/index.tsx';
import { useDocumentTitle } from '../../core/hooks.ts';

/** Acceso y creación de cuenta mediante correo verificado. */
export function LoginPage({ sessionNotice }: { sessionNotice?: string } = {}) {
  const { auth, mode } = useServices();
  useDocumentTitle('Entrar');
  const [readNotice, setReadNotice] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'sent'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const onError = (e: Event) => setError((e as CustomEvent<string>).detail);
    window.addEventListener('promptcut:auth-error', onError);
    return () => window.removeEventListener('promptcut:auth-error', onError);
  }, []);

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    if (!readNotice && mode !== 'demo') return;
    setBusy(true);
    setError(null);
    try {
      await auth.requestEmailCode(email.trim());
      setStep('sent');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.verifyEmailCode(email.trim(), code.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="content" style={{ maxWidth: 440, margin: '8vh auto' }}>
      <div className="brand" style={{ fontSize: '1.2rem', marginBottom: 16 }}>
        <span className="brand-mark" aria-hidden="true">P</span> PromptCut
      </div>
      <div className="card stack">
        <h1 style={{ fontSize: '1.25rem' }}>Entrar</h1>
        {sessionNotice && <Notice tone="warn">{sessionNotice}</Notice>}
        {mode === 'demo' && <Notice tone="sim">Modo demostración: cualquier correo y el código 000000. Datos simulados, separados de cualquier cuenta real.</Notice>}
        {step === 'email' ? (
          <form className="stack" onSubmit={sendCode}>
            <TextInput label="Correo" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} help="Recibirás un enlace para entrar sin contraseña. Si es tu primera vez, crearemos tu cuenta al verificar el correo." error={error} />
            <LegalDocuments /><Checkbox label="He leído cómo se utiliza mi correo para verificar el acceso y crear la cuenta si es nueva." checked={readNotice} onChange={e=>setReadNotice(e.target.checked)} /><Button type="submit" variant="primary" disabled={busy || !email || (!readNotice && mode !== 'demo')}>{busy ? 'Enviando…' : mode === 'demo' ? 'Continuar con código de prueba' : 'Enviar enlace de acceso'}</Button>
          </form>
        ) : (
          <div className="stack">
            <h2>Revisa tu correo</h2>
            <p>Enviamos un enlace de acceso a <strong>{email}</strong>.</p>
            <p className="muted">{window.__ESTUDIO_DESKTOP__?.emailLink ? 'Mantén PromptCut abierto y pulsa el enlace del mensaje. Volverás a esta aplicación automáticamente.' : 'Abre el enlace en este mismo navegador.'} No necesitas escribir un código si usas el enlace.</p>
            {error && <Notice tone="warn">{error}</Notice>}
            <details open={mode === 'demo' || undefined}>
              <summary>Mi correo también incluye un código</summary>
              <form className="stack" onSubmit={verify}>
                <TextInput label="Código de acceso (alternativa al enlace)" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
                <Button type="submit" variant="primary" disabled={busy || code.length < 6}>{busy ? 'Verificando…' : 'Entrar con código'}</Button>
              </form>
            </details>
            <div className="row">
              <Button onClick={(e) => void sendCode(e)} disabled={busy}>Reenviar enlace</Button>
              <Button onClick={() => setStep('email')} disabled={busy}>Cambiar correo</Button>
            </div>
          </div>
        )}
      </div>
      <p className="muted small" style={{ marginTop: 16 }}>No necesitas invitación ni contraseña. Tu cuenta tiene su propio espacio de trabajo.</p>
    </main>
  );
}
